import React, { Suspense, useEffect } from 'react'
import { Toaster } from '@/components/ui/toaster'
import { SRAnnouncerProvider } from '@/components/ui/sr-announcer'
import { SkipLink } from '@/components/ui/skip-link'
import { ThemeProvider } from '@/contexts/ThemeContext'
import { ProgressProvider } from '@/contexts/ProgressContext'
import { LastStepProvider } from '@/contexts/LastStepContext'
import { ApplicationLoadingProvider } from '@/contexts/ApplicationLoadingContext'
import { QueryParamsProvider } from '@/contexts/QueryParamsContext'
import { buildEnvironmentConfig, envLog } from '@/config/env'
import { ApplicationLoadingScreen } from './components/ui/ApplicationLoadingScreen'
import { translate } from 'yardi-react-translation/translationService'
import { setConfigurableFormsConfig } from '@ysi/configurable-forms-react'
import '@ysi/configurable-forms-react/styles'
import { apiClientSecure } from '@/lib/api/index'
import { getPropertyId } from '@/hooks/useApiSequencing'

// Lazy load the main routing component to reduce initial bundle
const LazyAppRoutes = React.lazy(() => import('./AppRoutes'))
const LazyHelpWidget = React.lazy(() => import('@/components/help-widget').then(m => ({ default: m.HelpWidget })))

// Minimal loading component
const AppLoading = () => (
  <div className="min-h-screen bg-background flex items-center justify-center">
    <ApplicationLoadingScreen message={translate('Loading application...')} />
  </div>
)

function App() {
  // Log app initialization
  React.useEffect(() => {
    envLog.info(`App initialized in ${buildEnvironmentConfig.env} mode`)
  }, [])

  // Configurable forms (versioned package) – same package used by OLE and Guarantor Portal
  useEffect(() => {
    const siteName = () => localStorage.getItem('sitename')?.replace(/"/g, '') ?? ''
    setConfigurableFormsConfig({
      apiClient: apiClientSecure,
      getPropertyId,
      getSiteName: () => siteName() || null,
      completedStepPageName: 'customformsections',
      buildRedirectUrl: ({ siteName: s, completedStepPageName }) =>
        `/guarantorportal/content3/${s}/gp/${completedStepPageName}`,
      getSectionBaseUrl: (stepName) =>
        `/guarantorportal/content3/${siteName()}/gp/${stepName || 'customformsections'}`,
      stepLabels: {
        guarantordetail: 'Guarantor Info',
        customformsections: 'Guarantor Info',
      },
    })
  }, [])
  
  return (
    <QueryParamsProvider>
      <ThemeProvider>
        <ProgressProvider>
          <LastStepProvider>
            <ApplicationLoadingProvider>
              <SRAnnouncerProvider>
                {/* Skip to main content link for keyboard accessibility */}
                <SkipLink />
                <Suspense fallback={<AppLoading />}>
                  <LazyAppRoutes />
                </Suspense>
                <Toaster />
                <Suspense fallback={null}>
                  <LazyHelpWidget />
                </Suspense>
              </SRAnnouncerProvider>
            </ApplicationLoadingProvider>
          </LastStepProvider>
        </ProgressProvider>
      </ThemeProvider>
    </QueryParamsProvider>
  )
}

export default App